import axios, { AxiosError, AxiosResponse, InternalAxiosRequestConfig } from 'axios';
import {
  getToken,
  getRefreshToken,
  setToken,
  setRefreshToken,
  isRefreshTokenExpired,
  removeToken,
  removeRefreshToken,
} from './common';

// Add custom request config property to Axios types
declare module 'axios' {
  export interface AxiosRequestConfig {
    skipAuth?: boolean;
    _retry?: boolean;
  }
}

// Global state để quản lý refresh token - SHARED giữa axios và auth.hook
let isRefreshing = false;
let refreshPromise: Promise<string | null> | null = null;
let refreshSubscribers: Array<(token: string) => void> = [];
let refreshFailureSubscribers: Array<() => void> = [];
let globalBaseURL: string = '';

// Export function để check xem đang refresh không
export function isTokenRefreshing(): boolean {
  return isRefreshing;
}

// Export function để đợi refresh xong
export async function waitForTokenRefresh(): Promise<string | null> {
  if (isRefreshing && refreshPromise) {
    return await refreshPromise;
  }
  return null;
}

// Function để thêm subscriber vào queue

// Function để thêm subscriber cho refresh failure

// Function để thông báo tất cả subscribers khi token được refresh
function onTokenRefreshed(token: string) {
  refreshSubscribers.forEach((cb) => cb(token));
  refreshSubscribers = [];
  refreshFailureSubscribers = []; // Clear failure subscribers khi refresh thành công
}

// Function để clear data và redirect về login khi refresh thất bại
function handleRefreshFailure() {
  // Clear tất cả tokens và data
  removeToken();
  removeRefreshToken();

  // Thông báo tất cả subscribers về refresh failure
  refreshFailureSubscribers.forEach((cb) => cb());
  refreshFailureSubscribers = [];
  refreshSubscribers = [];

  // Redirect về trang login
  if (typeof window !== 'undefined') {
    window.location.href = '/login';
  }
}

// Function để refresh token - CHỈ GỌI 1 LẦN DUY NHẤT
async function refreshAccessToken(baseURL?: string): Promise<string | null> {
  const apiBaseURL = baseURL || globalBaseURL || import.meta.env.VITE_API_URL;
  // Nếu đang refresh, return promise hiện tại
  if (isRefreshing && refreshPromise) {
    return refreshPromise;
  }

  // Bắt đầu refresh
  isRefreshing = true;

  refreshPromise = (async () => {
    try {
      const refreshToken = getRefreshToken();

      if (!refreshToken || isRefreshTokenExpired(refreshToken)) {
        throw new Error('No valid refresh token');
      }

      // Tạo một axios instance mới không có interceptor để tránh loop
      const refreshAxios = axios.create({
        baseURL: apiBaseURL,
      });

      const response = await refreshAxios.post('/api/v1/auths/sessions/refresh', {
        refreshToken: refreshToken,
      });

      const { content, succeeded } = response.data;

      if (succeeded && content?.accessToken) {
        const newAccessToken = content.accessToken;
        const newRefreshToken = content.refreshToken;

        // Lưu token mới vào localStorage
        setToken(newAccessToken);
        if (newRefreshToken) {
          setRefreshToken(newRefreshToken);
        }

        // Đảm bảo token đã được lưu vào localStorage trước khi return
        // Đọc lại để verify
        const savedToken = getToken();
        if (savedToken !== newAccessToken) {
          // Nếu token chưa được lưu, thử lại
          setToken(newAccessToken);
        }

        return newAccessToken;
      }

      throw new Error('Refresh failed');
    } catch (error) {
      console.error('❌ Token refresh error:', error);
      return null;
    } finally {
      isRefreshing = false;
      refreshPromise = null;
    }
  })();

  return refreshPromise;
}

function getAxios(baseURL: string) {
  globalBaseURL = baseURL;
  const ins = axios.create({
    baseURL,
  });

  // Request interceptor
  ins.interceptors.request.use(
    function (config: InternalAxiosRequestConfig) {
      if (!config.headers) {
        config.headers = {} as any;
      }

      // If sending FormData (multipart), let the browser/axios set the correct Content-Type with boundary.
      // Do NOT force application/json here, otherwise file uploads will break.
      const isFormData =
        typeof FormData !== 'undefined' && (config as any).data instanceof FormData;
      if (isFormData) {
        // Preserve Authorization header logic below, but avoid overriding content-type.
        delete (config.headers as any)['Content-Type'];
      }

      config.headers["ngrok-skip-browser-warning"] = "true";

      // Skip auth for refresh token endpoint to prevent infinite loop
      if (
        config.skipAuth ||
        config.url?.includes('/refresh') ||
        config.url?.includes('/refresh_token')
      ) {
        (config.headers as any)['Content-Type'] = 'application/json';
        return config;
      }

      // Nếu đã có Authorization header (từ retry sau refresh), không ghi đè
      if ((config.headers as any)['Authorization']) {
        if (!isFormData) {
          (config.headers as any)['Content-Type'] = 'application/json';
        }
        return config;
      }

      // Đọc token hiện tại và set vào header
      // KHÔNG refresh ở đây - để response interceptor xử lý khi gặp 401
      const accessToken = getToken();
      if (accessToken) {
        (config.headers as any)['Authorization'] = `Bearer ${accessToken}`;
      }

      if (!isFormData) {
        (config.headers as any)['Content-Type'] = 'application/json';
      }
      return config;
    },
    function (error) {
      return Promise.reject(error);
    },
  );

  // Response interceptor for handling token refresh
  ins.interceptors.response.use(
    function (response: AxiosResponse) {
      return response;
    },
    async function (error: AxiosError) {
      const originalRequest = error.config as InternalAxiosRequestConfig & { _retry?: boolean };

      // Skip refresh logic for refresh token endpoint to prevent infinite loop
      if (
        originalRequest?.skipAuth ||
        originalRequest?.url?.includes('/refresh') ||
        originalRequest?.url?.includes('/refresh_token') ||
        originalRequest?.url?.includes('/login') ||
        originalRequest?.url?.includes('/register')
      ) {
        return Promise.reject(error);
      }

      // Nếu lỗi không phải 401 hoặc đã retry rồi, reject ngay
      if (error.response?.status !== 401 || originalRequest?._retry) {
        return Promise.reject(error);
      }

      // Nếu đang refresh, đợi refresh xong rồi retry
      if (isRefreshing && refreshPromise) {
        return new Promise((resolve, reject) => {
          // Đợi refresh promise hiện tại
          refreshPromise!
            .then((newToken) => {
              if (newToken) {
                // Đảm bảo token mới đã được lưu vào localStorage
                // Đọc lại token mới nhất từ localStorage để đảm bảo
                const latestToken = getToken();
                const tokenToUse = latestToken || newToken;

                // Thông báo tất cả subscribers
                onTokenRefreshed(tokenToUse);

                // Retry request với token mới - đảm bảo set header trước khi retry
                if (originalRequest.headers) {
                  originalRequest.headers['Authorization'] = `Bearer ${tokenToUse}`;
                }

                // Tạo một config mới để đảm bảo request interceptor không ghi đè token
                const retryConfig = {
                  ...originalRequest,
                  headers: {
                    ...originalRequest.headers,
                    Authorization: `Bearer ${tokenToUse}`,
                  },
                };

                ins
                  .request(retryConfig)
                  .then((response) => resolve(response))
                  .catch((err) => reject(err));
              } else {
                // Refresh thất bại
                handleRefreshFailure();
                reject(new Error('Token refresh failed'));
              }
            })
            .catch((err) => {
              handleRefreshFailure();
              reject(err);
            });
        });
      }

      // Bắt đầu refresh
      originalRequest._retry = true;

      try {
        const newToken = await refreshAccessToken(baseURL);

        if (newToken) {
          // Đảm bảo token mới đã được lưu vào localStorage
          // Đọc lại token mới nhất từ localStorage để đảm bảo
          const latestToken = getToken();
          const tokenToUse = latestToken || newToken;

          // Thông báo tất cả subscribers (nếu có)
          onTokenRefreshed(tokenToUse);

          // Retry request ban đầu với token mới - đảm bảo set header trước khi retry
          if (originalRequest.headers) {
            originalRequest.headers['Authorization'] = `Bearer ${tokenToUse}`;
          }

          // Tạo một config mới để đảm bảo request interceptor không ghi đè token
          const retryConfig = {
            ...originalRequest,
            headers: {
              ...originalRequest.headers,
              Authorization: `Bearer ${tokenToUse}`,
            },
          };

          return ins.request(retryConfig);
        } else {
          // Refresh thất bại - clear data và redirect về login
          handleRefreshFailure();
          return Promise.reject(error);
        }
      } catch (refreshError) {
        // Refresh thất bại - clear data và redirect về login
        handleRefreshFailure();
        return Promise.reject(refreshError);
      }
    },
  );

  return ins;
}

// Ensure VITE_API_URL is defined
if (!import.meta.env.VITE_API_URL) {
  throw new Error('VITE_API_URL environment variable is not defined');
}

// Create an axios instance with the base URL
export const API = getAxios(`${import.meta.env.VITE_API_URL}`);

// Export refreshAccessToken để sử dụng ở nơi khác (như auth.hook.ts)
export { refreshAccessToken };
